import { ArrowDownRight, ArrowUpRight } from "lucide-react"

import { formatCurrency } from "../../lib/money"
import { cn } from "../../lib/utils"

type RevenueTrendProps = {
  current: number
  previous: number
  /** Tooltip context, e.g. "vs previous 7 days". */
  comparisonLabel: string
  /** Currency the previous figure is quoted in inside the tooltip. */
  currency?: string
}

/** Period-over-period change for a HEADLINE revenue figure: a filled pill
 *  carrying an explicit sign, sub-percent precision below 10 %, and the
 *  previous amount in its tooltip. The quieter `MetricTrend` is the one for
 *  secondary card metrics — this one is deliberately louder, so keep it for the
 *  single number a page leads with.
 *
 *  When the previous window was empty there is no ratio to form: a non-zero
 *  current reads as "new"; a still-empty current renders nothing. */
export function RevenueTrend({
  current,
  previous,
  comparisonLabel,
  currency = "EUR",
}: RevenueTrendProps) {
  const title = `${comparisonLabel} (${formatCurrency(previous, currency, 0)})`

  if (previous === 0) {
    if (current === 0) {
      return null
    }

    return <RevenueTrendChip direction="up" label="new" title={title} />
  }

  const percentChange = ((current - previous) / previous) * 100
  if (percentChange === 0) {
    return (
      <span
        className="rounded-full bg-muted px-2.5 py-1 text-xs font-medium tabular-nums text-muted-foreground"
        title={title}
      >
        0%
      </span>
    )
  }

  const direction = percentChange > 0 ? "up" : "down"
  return (
    <RevenueTrendChip
      direction={direction}
      label={`${direction === "up" ? "+" : "-"}${formatTrendPercent(percentChange)}`}
      title={title}
    />
  )
}

function RevenueTrendChip({
  direction,
  label,
  title,
}: {
  direction: "up" | "down"
  label: string
  title: string
}) {
  const Icon = direction === "up" ? ArrowUpRight : ArrowDownRight
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold tabular-nums",
        direction === "up" ? "bg-success/10 text-success" : "bg-destructive/10 text-destructive"
      )}
      title={title}
    >
      <Icon className="size-3.5" />

      {label}
    </span>
  )
}

function formatTrendPercent(value: number): string {
  const absoluteValue = Math.abs(value)
  const maximumFractionDigits = absoluteValue < 10 ? 1 : 0
  return `${absoluteValue.toLocaleString(undefined, {
    maximumFractionDigits,
    minimumFractionDigits: absoluteValue < 1 ? 1 : 0,
  })}%`
}
